import React, {Fragment} from "react";
import {PlayerCategory} from "data/enums";
import fireIcon from "assets/img/icons/fire.png";
import starIcon from "assets/img/icons/star.svg";
import styled from "@emotion/styled";
import isPropValid from "@emotion/is-prop-valid";
import {css} from "@mui/material/styles";
import {motion, Variants, Variant} from "framer-motion";

const ImgCategory = styled(motion.img, {
	shouldForwardProp: (prop) => isPropValid(prop) && prop !== "isHidden",
})<{isHidden?: boolean}>`
	width: 16px;
	transform-origin: center;
	${(props) =>
		props.isHidden &&
		css`
			opacity: 0;
			visibility: hidden;
		`}
`;

interface IProps {
	category: PlayerCategory;
	isHidden?: boolean;
}

export const PlayerCategoryImage: React.FC<IProps> = ({category, isHidden}) => {
	return (
		<Fragment>
			{
				{
					[PlayerCategory.HOT]: (
						<ImgCategory
							isHidden={isHidden}
							src={fireIcon}
							className={`icon-${category}`}
							alt="Hot player icon"
						/>
					),
					[PlayerCategory.COLD]: null,
					[PlayerCategory.NORMAL]: null,
					[PlayerCategory.STAR]: (
						<ImgCategory
							isHidden={isHidden}
							src={starIcon}
							className={`icon-${category}`}
							alt="Star player icon"
						/>
					),
				}[category]
			}
		</Fragment>
	);
};

const WrapperArc = styled.div`
	width: 100%;
	height: 100%;

	img {
		&:nth-of-type(1),
		&:nth-of-type(7) {
			width: 8px;
			transform: translateY(44px) rotate(var(--rotate, 0deg));
		}

		&:nth-of-type(2),
		&:nth-of-type(6) {
			width: 13px;
			transform: translateY(26px) rotate(var(--rotate, 0deg));
		}

		&:nth-of-type(3),
		&:nth-of-type(5) {
			width: 16px;
			transform: translateY(11px) rotate(var(--rotate, 0deg));
		}

		&:nth-of-type(3) {
			margin-left: 6px;
			margin-right: 8px;
			transform: rotate(var(--rotate, 0deg));
		}

		&:nth-of-type(5) {
			margin-left: 8px;
			margin-right: 6px;
			transform: rotate(var(--rotate, 0deg));
		}

		&:nth-of-type(4) {
			width: 24px;
			height: 24px;
			transform: rotate(var(--rotate, 0deg));
		}
	}
`;

const Animation = styled(motion.div)`
	width: 100%;
	height: 100%;
`;

interface IPropsArc {
	category?: PlayerCategory;
	hideMiddleIcon?: boolean;
	animate?: boolean;
	infinity?: boolean;
	delay?: number;
}

export const PlayerCategoryArc: React.FC<IPropsArc> = ({
	category,
	animate,
	infinity = true,
	delay = 0,
}) => {
	if (!category) {
		return null;
	}

	const repeat = infinity ? Infinity : 0;

	const animationVariants: Variants = {
		[PlayerCategory.HOT]: {
			initial: {scale: 0.9},
			animate: {
				scale: [0.9, 0.9, 0.9, 0.9, 1.18, 1.17, 1.18, 0.9, 0.9, 0.9, 0.9],
				transition: {
					repeat,
					duration: 3,
					repeatType: "loop",
					delay,
				},
			} as Variant,
			exit: {scale: 0.9},
		},
		[PlayerCategory.NORMAL]: {
			initial: {scale: 0.9},
			animate: {
				scale: [0.9, 0.9, 1.18, 1.18, 1.18, 1.17, 1.18, 1.18, 1.18, 0.9, 0.9],
				transition: {
					repeat,
					duration: 3,
					repeatType: "loop",
					delay,
				},
			} as Variant,
			exit: {scale: 0.9},
		},
		[PlayerCategory.COLD]: {
			initial: {scale: 0.9},
			animate: {
				scale: [0.9, 0.9, 0.9, 0.9, 1.18, 1.17, 1.18, 0.9, 0.9, 0.9, 0.9],
				transition: {
					repeat,
					duration: 3,
					repeatType: "loop",
					delay,
				},
			} as Variant,
			exit: {scale: 0.9},
		},
		[PlayerCategory.STAR]: {
			initial: {scale: 0.9},
			animate: {
				scale: [0.9, 0.9, 1.18, 1.18, 1.18, 1.17, 1.18, 0.9, 0.9, 0.9, 0.9],
				"--rotate": [
					"0turn",
					"0turn",
					"0turn",
					"0turn",
					"0.33turn",
					"0.66turn",
					"1turn",
					"1turn",
					"1turn",
					"1turn",
					"1turn",
				],
				transition: {
					repeat,
					duration: 3.5,
					repeatType: "loop",
					delay,
				},
			} as Variant,
			exit: {scale: 0.9},
		},
	}[category];

	return (
		<WrapperArc>
			<Animation
				initial={"initial"}
				variants={animationVariants}
				animate={animate ? "animate" : "initial"}>
				<PlayerCategoryImage category={category} />
				<PlayerCategoryImage category={category} />
				<PlayerCategoryImage category={category} />
				<PlayerCategoryImage category={category} />
				<PlayerCategoryImage category={category} />
				<PlayerCategoryImage category={category} />
				<PlayerCategoryImage category={category} />
			</Animation>
		</WrapperArc>
	);
};
